// page-meeting-generation.ts — Onglet « Générer les actions » de la fiche
// réunion : table des brouillons (publier / modifier / supprimer), bloc
// « Analyser un CR » et CTA d'initialisation en masse (projet, comité,
// structure de la réunion).
//
// Deuxième tranche de l'extraction des closures de `renderMeetingPage`
// (audit dette technique 2026-09-07, point 16). Dix fonctions vivaient dans
// la god-function et lisaient son état par capture : l'id de la réunion, les
// utilisateurs et projets (pour les colonnes de la table), le verrou
// « réunion fermée », et les rattachements courants de l'entête (projet,
// comité, structure), lus au clic et non au rendu — d'où le getter
// `getLinks` dans `GenerationContext`, qui conserve ce comportement.
//
// Le markup de l'onglet (`renderGenerationTab`) reste dans la page : il
// dépend des libellés de projet / comité / structure. Ce module ne possède
// que le contenu de `#mp-gen-host` et le câblage des blocs.

import type { DraftActionItem } from './page-meeting-draft-actions.js';

export interface GenerationUser {
  id: number;
  trigram: string;
  name: string;
}

export interface GenerationProject {
  id: number;
  code: string;
  name: string;
}

/** Rattachements de l'entête au moment du clic (les selects du bloc
 * Informations peuvent avoir changé depuis le rendu de l'onglet). */
export interface MeetingLinks {
  projectId: number;
  committeeId: number;
  structureId: number;
}

export interface GenerationContext {
  meetingId: number;
  users: ReadonlyArray<GenerationUser>;
  projects: ReadonlyArray<GenerationProject>;
  isClosed: boolean;
  getLinks: () => MeetingLinks;
}

export interface GenerationTab {
  /** (Re)charge et rend la table des brouillons dans `#mp-gen-host`. */
  load(): Promise<void>;
  /** Câble le bloc « Analyser un CR » et les CTA d'initialisation en masse. */
  wire(host: HTMLElement): void;
}

/** Id DOM du conteneur de la table, posé par `renderGenerationTab`. */
export const GEN_HOST_ID = 'mp-gen-host';

/** Navigation vers la page d'édition complète d'une action, en préservant le
 * contexte de retour (onglet Génération actions de cette réunion). Le
 * backHash est consommé par page-action.ts → onCancel / onSaved. */
export function navigateToActionEditor(meetingId: number, actionId: number): void {
  try {
    sessionStorage.setItem('actionEditor:backHash', `#meeting/${meetingId}?tab=generation`);
  } catch {
    /* sessionStorage indisponible : fallback page-action.ts → #actions */
  }
  window.location.hash = '#action/' + actionId;
}

function ensureGenFadeoutStyles(): void {
  if (document.getElementById('mp-gen-fadeout-styles')) return;
  const s = document.createElement('style');
  s.id = 'mp-gen-fadeout-styles';
  s.textContent = `
      @keyframes mp-gen-fadeout {
        0%   { opacity:1; transform:translateX(0); background:#dcfce7 }
        100% { opacity:0; transform:translateX(40px); background:#dcfce7 }
      }
      tr.mp-gen-fadeout, tr.mp-gen-fadeout > td {
        animation: mp-gen-fadeout 0.3s ease-in forwards;
      }
    `;
  document.head.appendChild(s);
}

/** Anime la disparition d'une ligne de la table générée puis la retire
 * physiquement du DOM. ~280 ms (cf. CSS .mp-gen-fadeout ci-dessus). */
export function fadeOutAndRemoveDraftRow(actionId: number): void {
  const host = document.getElementById(GEN_HOST_ID);
  if (!host) return;
  const tr = host.querySelector<HTMLElement>(`tbody tr[data-action-id="${actionId}"]`);
  if (!tr) return;
  ensureGenFadeoutStyles();
  tr.classList.add('mp-gen-fadeout');
  window.setTimeout(() => tr.remove(), 320);
}

function ensureAnalyseOverlayStyles(): void {
  if (document.getElementById('mp-analyse-overlay-styles')) return;
  const s = document.createElement('style');
  s.id = 'mp-analyse-overlay-styles';
  s.textContent = `
      @keyframes mp-gen-fadein {
        0%   { background:#fef9c3; transform: translateX(-12px); opacity:0.4; }
        50%  { background:#fef9c3; transform: translateX(0); opacity:1; }
        100% { background:transparent; transform: translateX(0); opacity:1; }
      }
      tr.mp-gen-new {
        animation: mp-gen-fadein 1.6s ease-out 1;
      }
    `;
  document.head.appendChild(s);
}

/** Anime les lignes nouvellement ajoutées dans la table du bloc « Générer les
 * actions ». Cherche les <tr data-action-id="X"> par id et applique la classe
 * mp-gen-new (fade-in + slide-in + flash jaune). */
export function highlightNewDraftRows(ids: ReadonlyArray<number>): void {
  if (!ids.length) return;
  ensureAnalyseOverlayStyles();
  const host = document.getElementById(GEN_HOST_ID);
  if (!host) return;
  for (const id of ids) {
    const tr = host.querySelector<HTMLElement>(`tr[data-action-id="${id}"]`);
    if (!tr) continue;
    tr.classList.add('mp-gen-new');
    window.setTimeout(() => tr.classList.remove('mp-gen-new'), 1800);
  }
}

// ── Bloc "Analyser un CR" en haut de l'onglet ───────────────────────────
// Variante factorisée du wizard standalone #actions → Analyser un CR.
// 1) parse du .docx/.pptx via /meeting/parse-docx
// 2) analyse Claude via /meeting/analyze
// 3) chaque action extraite est créée comme draft (to_validate=True) liée
//    à la réunion via /meetings-records/{id}/draft-actions
// 4) rafraîchissement de la table juste en-dessous (`reload`)
function wireAnalyseCr(host: HTMLElement, meetingId: number, reload: () => Promise<void>): void {
  const fileInput = host.querySelector<HTMLInputElement>('#mp-analyse-file');
  const fileLabel = host.querySelector<HTMLElement>('#mp-analyse-file-label');
  const launchBtn = host.querySelector<HTMLButtonElement>('#mp-analyse-launch');
  const statusEl = host.querySelector<HTMLElement>('#mp-analyse-status');
  if (!fileInput || !launchBtn) return;

  let picked: File | null = null;
  fileInput.addEventListener('change', () => {
    picked = fileInput.files?.[0] || null;
    if (fileLabel)
      fileLabel.textContent = picked
        ? `📎 ${picked.name}`
        : t('pageMeeting.generation.analyse.pickFile');
    launchBtn.disabled = !picked;
    if (statusEl) statusEl.textContent = '';
  });

  launchBtn.addEventListener('click', () => {
    if (!picked) {
      toast(t('pageMeeting.generation.analyse.errFile'), 'error');
      return;
    }
    const file = picked;
    void (async () => {
      launchBtn.disabled = true;
      // Overlay de progression — fourni par le composant partagé
      // `componentCrAnalysis`, qui pilote aussi le pipeline parse/IA/création.
      const overlay = window.componentCrAnalysis.showAnalyseOverlay(
        t('pageMeeting.generation.analyse.parsing'),
        t('pageMeeting.generation.analyse.title'),
      );
      const setStep = (msg: string): void => {
        const lbl = overlay.querySelector<HTMLElement>('.cmp-cr-step');
        if (lbl) lbl.textContent = msg;
        if (statusEl) statusEl.textContent = msg;
      };
      // Capture des ids existants pour pouvoir détecter les nouveaux
      // après refresh (et animer uniquement les nouveaux).
      const beforeIds = new Set<number>();
      try {
        const existing = (await api.listMeetingDraftActions(meetingId)) as Array<{
          id: number;
        }>;
        existing.forEach((d) => beforeIds.add(d.id));
      } catch {
        /* non bloquant : pas d'animation au pire */
      }
      try {
        const result = await window.componentCrAnalysis.runCrAnalysis({
          file,
          callbacks: { setStep },
          createAction: async (action) =>
            (await api.createMeetingDraftAction(meetingId, {
              text: action.text,
              deadline: action.echeanceIso,
              performers: action.performers,
              priority: action.priority,
            })) as { id: number },
        });
        if (!result.total) {
          overlay.remove();
          if (statusEl) statusEl.textContent = '';
          toast(t('pageMeeting.generation.analyse.noActions'), 'info');
          return;
        }
        setStep(t('pageMeeting.generation.analyse.done', { n: result.done }));
        toast(
          t('pageMeeting.generation.analyse.done', { n: result.done }),
          result.done ? 'ok' : 'info',
        );
        // Reset du picker.
        picked = null;
        fileInput.value = '';
        if (fileLabel) fileLabel.textContent = t('pageMeeting.generation.analyse.pickFile');
        // Refresh + détection des nouveaux ids pour les animer.
        await reload();
        try {
          const afterRaw = (await api.listMeetingDraftActions(meetingId)) as Array<{
            id: number;
          }>;
          const addedIds = afterRaw.map((d) => d.id).filter((id) => !beforeIds.has(id));
          highlightNewDraftRows(addedIds);
        } catch {
          /* non bloquant */
        }
        overlay.remove();
      } catch (err: unknown) {
        overlay.remove();
        if (statusEl) statusEl.textContent = '';
        toast(err instanceof Error ? err.message : t('common.error'), 'error');
      } finally {
        launchBtn.disabled = !picked;
      }
    })();
  });
}

export function createGenerationTab(ctx: GenerationContext): GenerationTab {
  const { meetingId, users, projects, isClosed, getLinks } = ctx;

  // Reprend exactement le même format de table que l'onglet "Principales
  // actions" (via componentActionRow) ; les colonnes secondaires (status,
  // RACI, projets…) sont simplement vides puisque les drafts ne portent
  // que text/approver_id/deadline. On post-traite chaque <tr> pour injecter
  // une cellule custom avec les 3 CTAs.
  async function load(): Promise<void> {
    const host = document.getElementById(GEN_HOST_ID);
    if (!host) return;
    let drafts: DraftActionItem[] = [];
    let actionStatuses: unknown[];
    try {
      [drafts, actionStatuses] = (await Promise.all([
        api.listMeetingDraftActions(meetingId),
        api.getActionStatuses(),
      ])) as [DraftActionItem[], unknown[]];
    } catch (err: unknown) {
      host.innerHTML = `<div class="error">${esc(err instanceof Error ? err.message : t('common.error'))}</div>`;
      return;
    }
    if (!drafts.length) {
      host.innerHTML = `<div class="empty-state" style="padding:24px;text-align:center;color:var(--text3)">${esc(t('pageMeeting.generation.empty'))}</div>`;
      return;
    }
    // Construit les lignes via componentActionRow (même format que
    // Principales actions). Les drafts sont synthétisés comme ActionLite ;
    // tous les champs avancés sont absents donc rendus vides.
    const userMap = Object.fromEntries(users.map((u) => [u.id, u]));
    const projectMap = Object.fromEntries(projects.map((p) => [p.id, p]));
    const uid = currentUserId();
    const opts = {
      statuses: actionStatuses,
      userMap,
      projectMap,
      readOnly: true,
      currentUserId: uid,
      userTeamIds: [],
    };
    // Cellule d'actions (Publier/Modifier/Supprimer) injectée AVANT le
    // `</tr>` directement dans le HTML string : nécessaire pour rester
    // cohérent avec le nombre de colonnes du header. Un `tr.appendChild`
    // post-render ajoutait une 11e cellule sans `<th>` correspondant,
    // ce qui décalait visuellement les CTAs (le browser repositionnait
    // la cellule "hors-table" au niveau du header).
    const customHeader = '<th style="width:140px"></th>';
    const headersHtml = window.componentActionRow.buildActionHeadersHtml() + customHeader;
    // En réunion fermée : on garde uniquement le bouton "Publier" (= valider
    // l'action et la sortir des drafts). Les CTAs Modifier / Supprimer sont
    // retirés — cohérent avec le verrou backend (PATCH/DELETE renvoient 403).
    // (B) Pastille « ? » = extrait du CR. Magasin partagé avec le wizard, non
    // persisté : la pastille disparaît après un rechargement de page.
    const crSrc = (aid: number): string => window.componentCrAnalysis.crSourceBadgeHtml(aid);
    const customCellHtml = (aid: number): string => {
      if (isClosed) {
        return `<td style="white-space:nowrap;padding:4px 6px;text-align:right;vertical-align:middle">
          ${crSrc(aid)}<button type="button" class="btn btn-sm btn-secondary mp-gen-publish" data-aid="${aid}" title="${esc(t('pageMeeting.generation.btnPublishTitle'))}">${esc(t('pageMeeting.generation.btnPublish'))}</button>
        </td>`;
      }
      return `<td style="white-space:nowrap;padding:4px 6px;text-align:right;vertical-align:middle">
        ${crSrc(aid)}<button type="button" class="btn btn-sm btn-secondary mp-gen-publish" data-aid="${aid}" title="${esc(t('pageMeeting.generation.btnPublishTitle'))}" style="margin-right:4px">${esc(t('pageMeeting.generation.btnPublish'))}</button>
        <button type="button" class="btn btn-secondary btn-icon mp-gen-edit" data-aid="${aid}" title="${esc(t('common.edit'))}" style="color:#147A57;margin-right:4px">✎</button>
        <button type="button" class="btn btn-danger btn-icon mp-gen-delete" data-aid="${aid}" title="${esc(t('common.delete'))}">✕</button>
      </td>`;
    };
    const rowsHtml = drafts
      .map((d) => {
        const a = {
          id: d.id,
          text: d.text,
          approver_id: d.approver_id,
          deadline: d.deadline,
          priority: d.priority ?? null,
          performers: d.performers || [],
          consulted: d.consulted || [],
          informed: d.informed || [],
          project_ids: d.project_ids || [],
        };
        const rowHtml = window.componentActionRow.buildActionRowHtml(a as never, opts as never);
        // Injecte la cellule custom avant `</tr>` final. Le replace ne
        // matche qu'une seule occurrence (la fermeture du <tr>).
        return rowHtml.replace('</tr>', customCellHtml(d.id) + '</tr>');
      })
      .join('');
    host.innerHTML = `<div class="table-wrap"><table class="table">${headersHtml}<tbody>${rowsHtml}</tbody></table></div>`;
    // Wiring : clic crayon, clic publish, clic delete, clic 2 1ères cellules.
    host.querySelectorAll<HTMLTableRowElement>('tbody tr').forEach((tr, idx) => {
      const draft = drafts[idx];
      if (!draft) return;
      const cells = tr.querySelectorAll<HTMLTableCellElement>('td');
      [cells[0], cells[1]].forEach((cell) => {
        if (!cell) return;
        cell.style.cursor = 'pointer';
        cell.addEventListener('click', (e) => {
          if ((e.target as HTMLElement).closest('button, a')) return;
          navigateToActionEditor(meetingId, draft.id);
        });
      });
    });
    host.querySelectorAll<HTMLButtonElement>('.mp-gen-edit').forEach((btn) => {
      btn.addEventListener('click', () => {
        const aid = parseInt(btn.dataset['aid'] || '0');
        if (aid) navigateToActionEditor(meetingId, aid);
      });
    });
    host.querySelectorAll<HTMLButtonElement>('.mp-gen-publish').forEach((btn) => {
      btn.addEventListener('click', () => {
        const aid = parseInt(btn.dataset['aid'] || '0');
        void publish(aid);
      });
    });
    host.querySelectorAll<HTMLButtonElement>('.mp-gen-delete').forEach((btn) => {
      btn.addEventListener('click', () => {
        const aid = parseInt(btn.dataset['aid'] || '0');
        if (!confirm(t('pageMeeting.generation.confirmDelete'))) return;
        void remove(aid);
      });
    });
  }

  async function publish(actionId: number): Promise<void> {
    if (!actionId) return;
    // Animation d'abord, suppression visuelle pendant que le serveur traite.
    fadeOutAndRemoveDraftRow(actionId);
    try {
      await api.publishMeetingDraftAction(meetingId, actionId);
      // La provenance ne sert qu'à arbitrer avant publication : on l'oublie.
      window.componentCrAnalysis.forgetCrSource(actionId);
      toast(t('pageMeeting.generation.toastPublished'), 'ok');
    } catch (err: unknown) {
      toast(err instanceof Error ? err.message : t('common.error'), 'error');
      // En cas d'erreur, on recharge l'onglet pour restaurer la ligne.
      void load();
    }
  }

  async function remove(actionId: number): Promise<void> {
    if (!actionId) return;
    try {
      await api.deleteMeetingDraftAction(meetingId, actionId);
      window.componentCrAnalysis.forgetCrSource(actionId);
      toast(t('common.saved'), 'ok');
      void load();
    } catch (err: unknown) {
      toast(err instanceof Error ? err.message : t('common.error'), 'error');
    }
  }

  // Câble les CTAs "Initialiser toutes les actions avec <projet>" /
  // "<comité>" / "<structure>". Confirm pour éviter les bulk-link
  // accidentels. Backend POST /draft-actions/bulk-link.
  function wireBulkLinks(host: HTMLElement): void {
    const runBulk = (
      payload: {
        project_id?: number | null;
        committee_id?: number | null;
        structure_id?: number | null;
      },
      confirmKey: string,
    ): void => {
      if (!confirm(t(confirmKey))) return;
      void (async () => {
        try {
          const res = (await api.bulkLinkMeetingDraftActions(meetingId, payload)) as {
            updated: number;
            total: number;
          };
          toast(
            t('pageMeeting.generation.bulk.toastDone', {
              n: res.updated,
              total: res.total,
            }),
            'ok',
          );
          // Refresh + flash sur les lignes touchées : ici on n'a pas la
          // liste des ids exacts modifiés, on rafraîchit simplement la
          // table. Un sur-clic ne re-flashera rien (idempotent côté API).
          void load();
        } catch (err: unknown) {
          toast(err instanceof Error ? err.message : t('common.error'), 'error');
        }
      })();
    };

    host.querySelector<HTMLButtonElement>('#mp-gen-bulk-project')?.addEventListener('click', () => {
      const { projectId } = getLinks();
      if (!projectId) return;
      runBulk({ project_id: projectId }, 'pageMeeting.generation.bulk.projectConfirm');
    });
    host
      .querySelector<HTMLButtonElement>('#mp-gen-bulk-committee')
      ?.addEventListener('click', () => {
        const { committeeId } = getLinks();
        if (!committeeId) return;
        runBulk({ committee_id: committeeId }, 'pageMeeting.generation.bulk.committeeConfirm');
      });
    host
      .querySelector<HTMLButtonElement>('#mp-gen-bulk-structure')
      ?.addEventListener('click', () => {
        const { structureId } = getLinks();
        if (!structureId) return;
        runBulk({ structure_id: structureId }, 'pageMeeting.generation.bulk.structureConfirm');
      });
  }

  function wire(host: HTMLElement): void {
    wireAnalyseCr(host, meetingId, load);
    wireBulkLinks(host);
  }

  return { load, wire };
}
